import type { StorageBrowserDisplayText } from "@aws-amplify/ui-react-storage/browser";

import type { ExtensionVerdict, UploadPolicy, UploadRules } from "./validation";
import {
  ANALYSIS_INPUT_RULES,
  extensionVerdict,
  isIndividualFile,
  isWithinSizeLimit,
  unpairedFiles,
} from "./validation";

const BYTES_PER_MEGABYTE = 1000 * 1000;
const MEGABYTES_PER_GIGABYTE = 1000;

/** Render a byte limit the way the folder's rules state it, for example `1 GB`. */
function sizeLimitLabel(maxSizeBytes: number): string {
  const megabytes = maxSizeBytes / BYTES_PER_MEGABYTE;
  return megabytes < MEGABYTES_PER_GIGABYTE
    ? `${String(megabytes)} MB`
    : `${String(megabytes / MEGABYTES_PER_GIGABYTE)} GB`;
}

export const DROP_ZONE_PROMPT = "Drag and drop files or click to browse";

function formatNames(rules: UploadRules): string {
  return rules.allowedExtensions
    .map((extension) => extension.slice(1))
    .join(", ");
}

/** The one-line summary of what the folder takes, shown under the drop-zone prompt. */
export function uploadLimitsLabel(rules: UploadRules): string {
  return `Supported formats: ${formatNames(rules)}`;
}

export const CANCEL_LABEL = "Cancel";
export const DONE_LABEL = "Done";
export const BACK_LABEL = "Back";

const ALREADY_UPLOADED_STATUS_LABEL = "Already uploaded";

const REJECTION_REASON = {
  insideFolder: "inside a folder",
  tooLarge: (maxSizeBytes: number): string =>
    `over ${sizeLimitLabel(maxSizeBytes)}`,
} as const;

/** What to tell the user about each verdict; an allowed type has nothing to report. */
const EXTENSION_REJECTION: Record<ExtensionVerdict, string | undefined> = {
  allowed: undefined,
  unsupported: "unsupported type",
  wrongCase:
    "the file type extension must be written in all lower case or all upper case",
};

const UPLOAD_MESSAGE = {
  uploaded: (count: number): string =>
    count === 1 ? "1 file uploaded." : `${String(count)} files uploaded.`,

  alreadyUploaded: (count: number): string =>
    count === 1
      ? "1 file is already in this folder and was not uploaded — rename it and upload again."
      : `${String(count)} files are already in this folder and were not uploaded — rename them and upload again.`,

  failed: (count: number): string =>
    count === 1
      ? "1 file failed to upload."
      : `${String(count)} files failed to upload.`,

  canceled: (count: number): string =>
    count === 1 ? "1 upload canceled." : `${String(count)} uploads canceled.`,

  rejectedFiles: (rejectedDetails: string, rules: UploadRules): string =>
    `Only individual ${rules.description} of ${sizeLimitLabel(rules.maxSizeBytes)} ` +
    `or smaller can be uploaded; folders are not supported. ` +
    `These files were not added: ${rejectedDetails}.`,
} as const;

function rejectionReason(file: File, rules: UploadRules): string {
  const reasons: string[] = [];
  if (!isIndividualFile(file)) {
    reasons.push(REJECTION_REASON.insideFolder);
  }
  const extensionProblem =
    EXTENSION_REJECTION[extensionVerdict(file.name, rules.allowedExtensions)];
  if (extensionProblem) {
    reasons.push(extensionProblem);
  }
  if (!isWithinSizeLimit(file.size, rules.maxSizeBytes)) {
    reasons.push(REJECTION_REASON.tooLarge(rules.maxSizeBytes));
  }
  return reasons.join(" and ");
}

export function rejectedFilesSummary(
  invalidFiles: readonly { readonly file: File }[] | undefined,
  rules: UploadRules = ANALYSIS_INPUT_RULES,
): string | undefined {
  if (!invalidFiles?.length) {
    return undefined;
  }

  const rejectedDetails = invalidFiles
    .map(({ file }) => `${file.name} (${rejectionReason(file, rules)})`)
    .join(", ");

  return UPLOAD_MESSAGE.rejectedFiles(rejectedDetails, rules);
}

export function unpairedFilesSummary(files: readonly File[]): string {
  return (
    "Each result must be uploaded in the same upload as its data quality report, " +
    "named <result file name>.data_quality.pdf or .md. Nothing was uploaded. " +
    `Missing a partner: ${files.map((file) => file.name).join(", ")}.`
  );
}

export function unpairedDropMessage(
  droppedFiles: readonly File[],
  rules: UploadRules,
): string | undefined {
  const unpaired = unpairedFiles(droppedFiles, rules);
  return unpaired.length > 0 ? unpairedFilesSummary(unpaired) : undefined;
}

export function unpairedListedFilesSummary(
  unpairedNames: readonly string[],
): string {
  return `Missing their data quality report or result: ${unpairedNames.join(", ")}.`;
}

export function createUploadDisplayText(
  uploadPolicy: UploadPolicy,
): StorageBrowserDisplayText {
  return {
    UploadView: {
      statusDisplayOverwritePreventedLabel: ALREADY_UPLOADED_STATUS_LABEL,

      getActionCompleteMessage: (data) => {
        const counts = data?.counts;
        const uploaded = counts?.COMPLETE ?? 0;
        const alreadyUploaded = counts?.OVERWRITE_PREVENTED ?? 0;
        const failed = counts?.FAILED ?? 0;
        const canceled = counts?.CANCELED ?? 0;

        const sentences = [
          uploaded > 0 ? UPLOAD_MESSAGE.uploaded(uploaded) : undefined,
          alreadyUploaded > 0
            ? UPLOAD_MESSAGE.alreadyUploaded(alreadyUploaded)
            : undefined,
          failed > 0 ? UPLOAD_MESSAGE.failed(failed) : undefined,
          canceled > 0 ? UPLOAD_MESSAGE.canceled(canceled) : undefined,
        ].filter((sentence): sentence is string => sentence !== undefined);

        if (sentences.length === 0) {
          return undefined;
        }

        const type =
          failed > 0
            ? "error"
            : alreadyUploaded > 0 || canceled > 0
              ? "warning"
              : "success";
        return { content: sentences.join(" "), type };
      },

      getFilesValidationMessage: (data) => {
        const content = rejectedFilesSummary(
          data?.invalidFiles,
          uploadPolicy.currentRules(),
        );
        return content ? { content, type: "error" } : undefined;
      },
    },
  };
}
